import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Redux action for sorting
import { handleSorting } from 'store/Admin/OrderList';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';

// Hooks & utils
import { useDeadlineFilter } from 'utils/hooks/useDeadlineFilter';
import { usePagination } from 'utils/hooks/usePagination';
import { useHistoryState } from 'utils/hooks/useHistoryState';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import { formatDate, formatDateShort } from 'utils/DateUtils';
import {
  ORDERSTATUS,
  ORDER_TYPE,
  pageNbOptions,
} from 'utils/Constants';
import { filterOrders } from 'helpers/FilterOrders';

// UI Components
import TablePagination from 'components/shared/Pagination/TablePagination';
import { Loader } from 'components/shared/Loader';
import ErrorComponent from 'components/shared/ErrorComponent';
import { CircularBar } from 'components/shared/CircularBar';
import { CustomDropdownReadOnly } from 'components/shared/DropDown/CustomDropdown';
import { CustomAutocomplete } from 'components/shared/DropDown/CustomAutocomplete';
import OrderTypeFilter from 'components/shared/Filter/OrderTypeFilter';
import DeadlineFilterPopover from 'components/shared/Filter/DeadlineFilterPopover';
import SearchIcon from 'icons/SearchIcon';
import { Button } from '@mui/material';
import DataTable from 'components/shared/DataTable';
import './ProjectTable.css';

/** Filtre Type simplifié pour ProjectTable : Premium, Smart ou Tous les projets */
const PROJECT_TYPE_FILTER_LIST = [
  { type: 'ALL', name: 'Tous les projets', text: 'Tous' },
  { type: 'PREMIUM', name: 'Premium', text: 'Premium' },
  { type: 'SMART', name: 'Smart', text: 'Smart' },
];

/** Filtre pour le type de contenu : Rédaction, Traduction ou Tous */
const CONTENT_TYPE_FILTER_LIST = [
  { type: 'ALL', name: 'Tous les types', text: 'Tous' },
  { type: 'EDITORIAL', name: 'Rédaction', text: 'Rédaction' },
  { type: 'TRANSLATION', name: 'Traduction', text: 'Traduction' },
];

/**
 * Returns true if we should show the "Type" column
 * (i.e. for all statuses except SMART and SMART_WITH_REVIEW)
 */
const shouldIncludeType = (status) =>
  ![ORDERSTATUS.SMART, ORDERSTATUS.SMART_WITH_REVIEW].includes(status);

/**
 * Returns true if we should show the "Temps restant" column
 * (for INPROGRESS, SMART, SMART_WITH_REVIEW)
 */
const shouldIncludeTempsRestant = (status) =>
  [
    ORDERSTATUS.INPROGRESS,
    ORDERSTATUS.SMART,
    ORDERSTATUS.SMART_WITH_REVIEW,
  ].includes(status);

/**
 * Builds the common/shared columns (title, deadline, writers, progress bars)
 */
const getCommonColumns = ({
  t,
  selectedYear,
  setSelectedYear,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedType,
  setSelectedType,
  handleTypeClick,
  handleTypeClose,
  anchorEl,
  handleWritersClick,
  handleWritersClose,
  anchorElWriters,
  handleManagerClick,
  handleManagerClose,
  anchorElManager,
  hideWritersAgentColumn,
}) => [
    {
      header: t('Titre du projet'),
      accessor: 'title',
      sortable: true,
      clickable: true, // ← seule colonne qui déclenche la navigation
      width: '20%',
      renderCell: (order) => (
        <span className="project-table-order-title" style={{ fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
          {order.title}
        </span>
      ),
    },
    {
      header: t('Chef de projet'),
      accessor: 'projectManager',
      sortable: false,
      width: '10%',
      renderCell: (order, idx) => {
        const managers = order?.projectManager ? [order.projectManager] : [];

        if (managers.length === 0) {
          return <Button disabled>{t('-')}</Button>;
        }

        return (
          <>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleManagerClick(idx, e);
              }}
              key={`btn-manager-${idx}`}
            >
              <img
                className="project-table-manager-icon"
                src="/Images/icon-users-down.png"
                alt={t('Manager')}
              />
            </Button>

            {anchorElManager?.[idx] && (
              <CustomDropdownReadOnly
                key={`dropdown-manager-${idx}`}
                handleClose={() =>
                  handleManagerClose(idx)
                }
                anchorEl={anchorElManager[idx]}
                list={managers}
                getOptionLabel={(opt) =>
                  opt?.mail ||
                  [opt?.firstName, opt?.lastName].filter(Boolean).join(' ') ||
                  `${t('ID')}: ${opt?.id}`
                }
              />
            )}
          </>
        );
      },
    },
    ...(!hideWritersAgentColumn ? [{
      header: t('Rédacteurs / Agent'),
      accessor: 'writers',
      sortable: false,
      width: '15%',
      renderCell: (order, idx) => {
        const writers = order?.writers || [];
        const agentAiName = order?.aiAgentName;

        if (agentAiName && agentAiName.trim() !== '') {
          return (
            <>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleWritersClick(idx, e);
                }}
                key={`btn-writers-${idx}`}
              >
                <img
                  className="project-table-writer-icon"
                  src="/Images/icon-users-down.png"
                  alt={t('Agent')}
                />
              </Button>

              {anchorElWriters?.[idx] && (
                <CustomDropdownReadOnly
                  key={`dropdown-writers-${idx}`}
                  handleClose={() =>
                    handleWritersClose(idx)
                  }
                  anchorEl={anchorElWriters[idx]}
                  list={[{ name: agentAiName, type: t('Agent IA') }]}
                  getOptionLabel={(opt) =>
                    opt.name || opt.mail || `${opt.firstName || ''} ${opt.lastName || ''}`.trim()
                  }
                />
              )}
            </>
          );
        }

        if (writers.length > 0) {
          return (
            <>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleWritersClick(idx, e);
                }}
                key={`btn-writers-${idx}`}
              >
                <img
                  className="project-table-writer-icon"
                  src="/Images/icon-users-down.png"
                  alt={t('Writers')}
                />
              </Button>

              {anchorElWriters?.[idx] && (
                <CustomDropdownReadOnly
                  key={`dropdown-writers-${idx}`}
                  handleClose={() =>
                    handleWritersClose(idx)
                  }
                  anchorEl={anchorElWriters[idx]}
                  list={writers}
                  getOptionLabel={(opt) =>
                    opt.mail || `${opt.firstName || ''} ${opt.lastName || ''}`.trim()
                  }
                />
              )}
            </>
          );
        }

        return <Button className="project-table-unassigned-button" disabled>{t('Non attribué')}</Button>;
      },
    }] : []),
    {
      header: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ flex: 1, textAlign: 'center' }}>{t('Deadline')}</span>
          <DeadlineFilterPopover
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </div>
      ),
      accessor: 'deadline',
      sortable: true,
      width: '10%',
      renderCell: (order) => (
        <span style={{ fontSize: 14, fontWeight: 500 }} className="project-table-deadline-value">
          {order.deadline}
        </span>
      ),
    },
    {
      header: t('Livrés'),
      accessor: 'deliveredTextsAmount',
      sortable: true,
      renderCell: (o) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500 }}>
          <img
            src="/Images/delivred-text-icon.png"
            alt={t('Livrés')}
            style={{ width: 16, height: 16 }}
          />
          <span className="project-table-counter-value">{o.deliveredTextsAmount || 0} / {o.textAmount || 0}</span>
        </div>
      ),
    },
    {
      header: t('Validés'),
      accessor: 'validatedTextsAmount',
      sortable: true,
      renderCell: (o) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500 }}>
          <img
            src="/Images/validate-text-icon.png"
            alt={t('Validés')}
            style={{ width: 16, height: 16 }}
          />
          <span className="project-table-counter-value">{o.validatedTextsAmount || 0} / {o.textAmount || 0}</span>
        </div>
      ),
    },
    {
      header: t('En ligne'),
      accessor: 'deployedTextsAmount',
      sortable: true,
      renderCell: (o) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500 }}>
          <img
            src="/Images/deployed-text-icon.png"
            alt={t('En ligne')}
            style={{ width: 16, height: 16 }}
          />
          <span className="project-table-counter-value">{o.deployedTextsAmount || 0} / {o.textAmount || 0}</span>
        </div>
      ),
    },
  ];

export default function ProjectTable({
  title,
  orderStatus,
  orders,
  isLoading,
  isError,
  fetchDisplayData,
  onRowClickRedirectPath,
  disableWebsiteFilter,
  hideWritersAgentColumn,
  hideTypeColumn,
  storageNamespace = 'AdminClient',
  hideTitle = false,
  hideSearch = false,
  hidePaginationSelector = false,
  hasNoProjectsAtAll = false,
  disableEmptyStateCreateProject = false,
  hideEmptyStateCreateProject = false,
  createProjectPath = '/AdminClient/CreateProject',
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [selectedType, setSelectedType] = useHistoryState(
    'selectedFilter',
    PROJECT_TYPE_FILTER_LIST[0]
  );
  const [selectedContentType, setSelectedContentType] = useHistoryState(
    'selectedContentType',
    CONTENT_TYPE_FILTER_LIST[0]
  );
  const [titleSearch, setTitleSearch] = useHistoryState('titleSearch', '');
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElContentType, setAnchorElContentType] = useState(null);
  const [anchorElWriters, setAnchorElWriters] = useState(null);
  const [anchorElManager, setAnchorElManager] = useState(null);
  const [titleFilteredOrders, setTitleFilteredOrders] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const { clients: clientsFromSelector } = useSelector(adminClientClientListSelector) || {};
  const clients = Array.isArray(clientsFromSelector) ? clientsFromSelector : [];
  const [selectedWebsiteUrl, setSelectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');

  useEffect(() => {
    if (clientsFromSelector == null) {
      dispatch(fetchAdminClientClientList());
    }
  }, [dispatch, clientsFromSelector]);

  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
  }, []);

  useEffect(() => {
    if (!orders) return;
    let byType = orders;
    if (selectedType?.type === 'PREMIUM') {
      byType = orders.filter(o => o.type === 'PREMIUM');
    } else if (selectedType?.type === 'SMART') {
      byType = orders.filter(o => o.type === 'SMART' || o.type === 'SMART_WITH_REVIEW');
    }

    let byContentType = byType;
    if (selectedContentType?.type === 'EDITORIAL') {
      byContentType = byType.filter(o => (o.textType || '').toString().toUpperCase() === 'EDITORIAL');
    } else if (selectedContentType?.type === 'TRANSLATION') {
      byContentType = byType.filter(o => (o.textType || '').toString().toUpperCase() === 'TRANSLATION');
    }

    let filtered = filterOrders(byContentType, { ...selectedType, type: '' }, titleSearch, true);
    if (!disableWebsiteFilter && selectedWebsiteUrl) {
      filtered = filtered.filter(o => (o.websiteUrl || '').toLowerCase() === selectedWebsiteUrl.toLowerCase());
    }
    if (titleSearch.length > 0) setCurrentPage(1);
    setTitleFilteredOrders(filtered);
  }, [orders, selectedType, selectedContentType, titleSearch, selectedWebsiteUrl]);

  const {
    filteredOrders,
    selectedYear,
    setSelectedYear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useDeadlineFilter(titleFilteredOrders);

  const { currentPage, setCurrentPage, totalPages, paginatedItems } =
    usePagination(filteredOrders, ordersPerPage);

  const [hoveredRowKey, setHoveredRowKey] = useState(null);

  const rows = paginatedItems.map((order) => {
    const rawDeadline = order.deadline;
    let deadlineTs = 0;
    if (rawDeadline) {
      const ts = new Date(rawDeadline).getTime();
      deadlineTs = Number.isNaN(ts) ? 0 : ts;
    }

    return {
      ...order,
      title: order.title,
      typeLabel: ORDER_TYPE.find((o) => o.type === order.type)?.name,
      deadline: rawDeadline ? formatDateShort(rawDeadline) : null,
      deadlineTs,
    };
  });

  const sortedRows = useMemo(() => {
    if (sortField !== 'deadline') {
      return rows;
    }

    const base = [...rows];
    base.sort((a, b) => {
      const da = a.deadlineTs || 0;
      const db = b.deadlineTs || 0;
      return da - db;
    });

    return sortOrder === 'asc' ? base : base.reverse();
  }, [rows, sortField, sortOrder]);

  const handleTypeClick = (idx, e) => setAnchorEl({ [idx]: e.currentTarget });
  const handleTypeClose = () => setAnchorEl(null);
  const handleContentTypeClick = (idx, e) => setAnchorElContentType({ [idx]: e.currentTarget });
  const handleContentTypeClose = () => setAnchorElContentType(null);
  const handleWritersClick = (idx, e) => setAnchorElWriters({ [idx]: e.currentTarget });
  const handleWritersClose = () => setAnchorElWriters(null);
  const handleManagerClick = (idx, e) => setAnchorElManager({ [idx]: e.currentTarget });
  const handleManagerClose = () => setAnchorElManager(null);

  const handleRowClick = (order) => {
    fetchDisplayData(order.id, order.type);
    try {
      window.localStorage.setItem(
        `${storageNamespace}_ProjectDisplay_fromCategory`,
        orderStatus
      );
      window.localStorage.setItem(
        `${storageNamespace}_ProjectDisplay_id`,
        String(order.id)
      );
    } catch { }
    if (onRowClickRedirectPath) {
      history.push(`${onRowClickRedirectPath}`);
    }
  };

  const toolbar = (
    <div className="box-info-header">
      {!hideTitle && (
        <div className="b-child-1">
          <h1>{title}</h1>
        </div>
      )}
      <div className="b-child-2 d-flex justify-content-end align-items-center">
        {!hideSearch && (
          <div className="box-input-text pe-0" style={{ marginRight: 12 }}>
            <div className="input-group input-cs w-100 pe-0">
              <div className="input-group-text">
                <SearchIcon />
              </div>
              <input
                type="text"
                className="form-control ps-0"
                value={titleSearch}
                onChange={(e) => setTitleSearch(e.target.value)}
                placeholder={t('Rechercher')}
              />
            </div>
          </div>
        )}
        {!disableWebsiteFilter && (
          <div className="box-input-text pe-0" style={{ width: 380, marginRight: 12 }}>
            <CustomAutocomplete
              list={[{ urlSite: '', name: t('Tous les sites') }, ...clients]}
              value={clients.find(w => (w.urlSite || '').toLowerCase() === selectedWebsiteUrl.toLowerCase()) || null}
              placeholder={t('Sélectionner un site')}
              getOptionLabel={(opt) => (opt ? (opt.urlSite || opt.name || '') : '')}
              setValue={(value) => {
                const newUrl = value && value.urlSite ? value.urlSite : '';
                setSelectedWebsiteUrl(newUrl);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
        {!hidePaginationSelector && (
          <div className="dropdown" style={{ width: '70px' }}>
            <button
              className="btn dropdown-toggle dropdown-custom w-100"
              type="button"
              id="dropdownMenuButton1"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {ordersPerPage}
              <img
                src="/Images/table-arrow.png"
                alt="open"
                style={{ width: 10, height: 10, marginLeft: 4 }}
              />
            </button>
            <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
              {pageNbOptions
                .filter((nb) => nb !== ordersPerPage)
                .map((item) => (
                  <li key={item}>
                    <button
                      className="dropdown-item"
                      onClick={() => setOrdersPerPage(item)}
                    >
                      {item}
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  const commonColumns = getCommonColumns({
    t,
    selectedYear,
    setSelectedYear,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedType,
    setSelectedType,
    handleTypeClick,
    handleTypeClose,
    anchorEl,
    handleWritersClick,
    handleWritersClose,
    anchorElWriters,
    handleManagerClose,
    handleManagerClick,
    anchorElManager,
    hideWritersAgentColumn
  });

  const typeColumn = {
    header: (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{t('Production')}</span>
        <OrderTypeFilter
          orderTypeList={PROJECT_TYPE_FILTER_LIST}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          handleClick={handleTypeClick}
          handleClose={handleTypeClose}
          anchorEl={anchorEl}
        />
      </div>
    ),
    accessor: 'typeLabel',
    sortable: false,
    width: '12%',
    renderCell: (order) => (
      <span className="project-table-production-value" style={{ fontSize: 14, fontWeight: 500 }}>
        {order.typeLabel}
      </span>
    ),
  };

  const contentTypeColumn = {
    header: (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{t('Type')}</span>
        <OrderTypeFilter
          orderTypeList={CONTENT_TYPE_FILTER_LIST}
          selectedType={selectedContentType}
          setSelectedType={setSelectedContentType}
          handleClick={handleContentTypeClick}
          handleClose={handleContentTypeClose}
          anchorEl={anchorElContentType}
        />
      </div>
    ),
    accessor: 'textType',
    sortable: false,
    width: '12%',
    renderCell: (order) => {
      const rawType = (order.textType || '').toString().toUpperCase();
      let label = order.textType || '-';

      if (rawType === 'EDITORIAL') {
        label = t('Rédaction');
      } else if (rawType === 'TRANSLATION') {
        label = t('Traduction');
      }

      return (
        <span className="project-table-type-value" style={{ fontSize: 14, fontWeight: 500 }}>
          {label}
        </span>
      );
    },
  };

  const columns = [
    commonColumns[0],
    ...(shouldIncludeType(orderStatus) && !hideTypeColumn ? [typeColumn] : []),
    contentTypeColumn,
    ...commonColumns.slice(1),
    ...(shouldIncludeTempsRestant(orderStatus) ? [] : []),
  ];

  const handleSortingChange = (field) => {
    const direction =
      field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    dispatch(
      handleSorting({
        table: orderStatus,
        sortField: field,
        sortOrder: direction,
      })
    );
    setSortField(field);
    setSortOrder(direction);
  };

  const emptyStateContent = hasNoProjectsAtAll ? (
    <div style={{
      textAlign: 'center',
      padding: '5px 5px',
      borderRadius: '8px',
      margin: '5px 0',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <div style={{ width: '100%', maxWidth: 'none' }}>
        <p style={{
          fontSize: '14px',
          color: '#666',
          lineHeight: '1.6',
          marginBottom: '5px',
          fontFamily: 'Poppins, sans-serif'
        }}>
          {t('Aucun projet créé pour le moment.')}
          {!hideEmptyStateCreateProject && (
            <>
              {' '}
              <button
                type="button"
                disabled={disableEmptyStateCreateProject}
                aria-disabled={disableEmptyStateCreateProject}
                onClick={() => {
                  if (disableEmptyStateCreateProject) return;
                  history.push(createProjectPath);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6458F1',
                  fontSize: '14px',
                  fontFamily: 'Poppins, sans-serif',
                  textDecoration: 'underline',
                  cursor: disableEmptyStateCreateProject ? 'not-allowed' : 'pointer',
                  opacity: disableEmptyStateCreateProject ? 0.5 : 1,
                  padding: 0,
                  margin: 0,
                  fontWeight: 'inherit'
                }}
                onMouseEnter={(e) => {
                  if (disableEmptyStateCreateProject) return;
                  e.target.style.color = '#5548d1';
                }}
                onMouseLeave={(e) => {
                  if (disableEmptyStateCreateProject) return;
                  e.target.style.color = '#6458F1';
                }}
              >
                {t('Lancer votre premier projet')}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  ) : undefined;

  return (
    <div className="content-center">
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <ErrorComponent />
      ) : (
        <>
          {(!hideTitle || !hideSearch || (!disableWebsiteFilter) || !hidePaginationSelector) && toolbar}

          <div
            style={{
              borderRadius: 12,
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
              backgroundColor: '#FAFAFA',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                }}
              >
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.accessor}
                        className={
                          col.accessor === 'deliveredTextsAmount'
                            ? 'project-table-progress-col project-table-progress-col-delivered'
                            : col.accessor === 'validatedTextsAmount'
                              ? 'project-table-progress-col project-table-progress-col-validated'
                              : col.accessor === 'deployedTextsAmount'
                                ? 'project-table-progress-col project-table-progress-col-online'
                                : ''
                        }
                        style={{
                          width: col.width || 'auto',
                          cursor: col.sortable ? 'pointer' : 'default',
                          padding: '8px 16px',
                          fontSize: 13,
                          color: '#818181',
                          backgroundColor: '#E9E9E9',
                          textAlign: 'left',
                          fontWeight: 400,
                          borderBottom: 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span
                            onClick={() => {
                              if (col.sortable) {
                                handleSortingChange(col.accessor);
                              }
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              cursor: col.sortable ? 'pointer' : 'default',
                            }}
                          >
                            {col.renderHeader ? col.renderHeader() : col.header}
                            {col.sortable && (
                              <span style={{ marginLeft: 4 }}>
                                <img
                                  src="/Images/table-arrow.png"
                                  alt="sort"
                                  style={{
                                    width: 12,
                                    height: 12,
                                    opacity: col.accessor === sortField ? 1 : 0.3,
                                    transform:
                                      col.accessor === sortField && sortOrder === 'desc'
                                        ? 'rotate(180deg)'
                                        : 'rotate(0deg)',
                                    transition: 'all 0.2s ease',
                                  }}
                                />
                              </span>
                            )}
                          </span>
                        </div>

                        {col.filter && (
                          <div style={{ marginTop: 4 }}>{col.filter}</div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 && emptyStateContent ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        style={{
                          padding: 0,
                          border: 'none',
                          backgroundColor: 'transparent',
                        }}
                      >
                        {emptyStateContent}
                      </td>
                    </tr>
                  ) : (
                    sortedRows.map((row, idx) => {
                      const rowKeyValue = row.id ?? idx;
                      return (
                        <tr
                          key={rowKeyValue}
                          style={{
                            backgroundColor:
                              hoveredRowKey === rowKeyValue ? '#F9FAFB' : '#FFFFFF',
                            transition: 'background-color 0.15s ease-out',
                            cursor: 'default', // ← plus de pointer sur toute la ligne
                          }}
                          onMouseEnter={() => setHoveredRowKey(rowKeyValue)}
                          onMouseLeave={() => setHoveredRowKey(null)}
                        // ← onClick supprimé du <tr>
                        >
                          {columns.map((col) => {
                            const key = `${rowKeyValue}-${col.accessor}`;
                            return (
                              <td
                                key={key}
                                className={
                                  col.accessor === 'deliveredTextsAmount'
                                    ? 'project-table-progress-col project-table-progress-col-delivered'
                                    : col.accessor === 'validatedTextsAmount'
                                      ? 'project-table-progress-col project-table-progress-col-validated'
                                      : col.accessor === 'deployedTextsAmount'
                                        ? 'project-table-progress-col project-table-progress-col-online'
                                        : ''
                                }
                                style={{
                                  width: col.width || 'auto',
                                  padding: '10px 16px',
                                  fontSize: 12,
                                  borderBottom: 'none',
                                  backgroundColor: 'inherit',
                                  // ← cursor pointer uniquement sur la colonne cliquable (titre)
                                  cursor: col.clickable ? 'pointer' : 'default',
                                }}
                                // ← onClick uniquement sur les colonnes marquées clickable
                                onClick={
                                  col.clickable && handleRowClick
                                    ? () => handleRowClick(row)
                                    : undefined
                                }
                              >
                                {col.renderCell
                                  ? col.renderCell(row, idx)
                                  : col.accessor
                                    ? row[col.accessor]
                                    : null}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {filteredOrders.length > 0 && (
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disablePrev={currentPage === 1}
              disableNext={currentPage === totalPages || filteredOrders.length === 0}
            />
          )}
        </>
      )}
    </div>
  );
}